import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db, useFirebaseAuthentication } from 'lib/firebase';
import { format } from 'date-fns';
import { Helmet } from 'react-helmet-async';
import SiteWrapper from '../../home-pages/SiteWrapper';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import CommentIcon from '@mui/icons-material/Comment';

const MomStoriesFeed = () => {
  const navigate = useNavigate();
  const user = useFirebaseAuthentication();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStories = async () => {
      try {
        // Query for answered questions
        // Note: We might need to create a composite index for this query: status == 'answered' + answerDate desc
        const q = query(
          collection(db, 'weekly_questions'),
          where('status', '==', 'answered'),
          orderBy('answerDate', 'desc')
        );

        const querySnapshot = await getDocs(q);
        const storiesData = await Promise.all(
          querySnapshot.docs.map(async (docSnapshot) => {
            // Fetch comment count for each story
            const commentsRef = collection(db, 'weekly_questions', docSnapshot.id, 'comments');
            const commentsSnapshot = await getDocs(commentsRef);
            
            return {
              id: docSnapshot.id,
              ...docSnapshot.data(),
              commentCount: commentsSnapshot.size
            };
          })
        );

        setStories(storiesData);
      } catch (err) {
        setError('Failed to load stories. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchStories();
  }, []);

  return (
    <SiteWrapper>
      <Helmet>
        <title>Mom's Stories - Family Memories</title>
      </Helmet>
      <Box sx={{ minHeight: '100vh', bgcolor: '#fff' }}>
        {/* Main Content Wrapper */}
        <Box sx={{ maxWidth: 1200, mx: 'auto', mb: 4, mt: 0, p: 4, borderRadius: 3, bgcolor: 'grey.50' }}>
          
          {/* Hero Section */}
          <Box sx={{ 
            textAlign: 'center', 
            mb: 4 
          }}>
            <Box
              component="img"
              src="/images/mom_stories_full.png"
              alt="Mom Stories"
              sx={{
                width: '100%',
                height: '400px',
                borderRadius: 3,
                objectFit: 'cover',
                boxShadow: 3,
                mb: 4,
              }}
            />
            <Typography variant="h1" sx={{ fontSize: '2.5rem', m: 0, fontWeight: 700, color: '#1e3a8a' }}>
              Mom's Stories
            </Typography>
            <Typography sx={{ mt: '10px', fontSize: '1.2rem', color: '#475569', mb: 3 }}>
              A collection of memories, wisdom, and stories from Mom.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/mom-stories/queue')}
                sx={{
                  borderColor: '#1e3a8a',
                  color: '#1e3a8a',
                  borderWidth: '2px',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#1e40af',
                    color: '#1e40af',
                    bgcolor: 'rgba(30, 58, 138, 0.05)',
                    borderWidth: '2px',
                  },
                }}
              >
                Suggest & Vote on Future Questions
              </Button>
              {user && user.email === 'rdymock@gmail.com' && (
                <Button
                  variant="text"
                  startIcon={<AdminPanelSettings />}
                  onClick={() => navigate('/mom-stories/admin')}
                  sx={{
                    color: '#64748b',
                    fontWeight: 600,
                    '&:hover': {
                      color: '#475569',
                      bgcolor: 'rgba(100, 116, 139, 0.1)',
                    },
                  }}
                >
                  Admin
                </Button>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Content */}
          {loading ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>
          ) : stories.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Typography variant="h6" color="text.secondary">
                No stories have been shared yet. Check back soon!
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {stories.map((story) => (
                <Grid item xs={12} md={6} lg={4} key={story.id}>
                  <Card 
                    elevation={3}
                    sx={{ 
                      bgcolor: 'common.white',
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6,
                      },
                      borderRadius: 3,
                    }}
                  >
                    <CardActionArea 
                      onClick={() => navigate(`/mom-stories/story/${story.id}`)}
                      sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
                    >
                      <CardContent sx={{ p: 3, width: '100%' }}>
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Chip 
                            label={story.answerDate ? format(story.answerDate.toDate(), 'MMM d, yyyy') : 'Unknown Date'} 
                            size="small" 
                            sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', color: '#667eea', fontWeight: 600 }}
                          />
                          {story.commentCount > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                              <CommentIcon sx={{ fontSize: 18 }} />
                              <Typography variant="caption" fontWeight={600}>
                                {story.commentCount}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Typography 
                          variant="h6" 
                          component="h3" 
                          gutterBottom
                          sx={{ 
                            fontWeight: 600,
                            minHeight: '3.2em', // Ensure roughly 2 lines of height for alignment
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {story.questionText}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            fontFamily: 'Georgia, serif',
                            lineHeight: 1.6,
                          }}
                        >
                          {story.momAnswer}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>
    </SiteWrapper>
  );
};

export default MomStoriesFeed;
